import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Check, Download, ArrowLeft, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

interface TriggerEntry {
    id: number;
    trigger: string;
    sensation: string;
    impulse: string;
    regulation: string;
}

export default function RecalibrationWorksheet() {
    const [triggers, setTriggers] = useState<TriggerEntry[]>(
        Array(5).fill(null).map((_, i) => ({
            id: i,
            trigger: '',
            sensation: '',
            impulse: '',
            regulation: ''
        }))
    );

    const [standards, setStandards] = useState<string[]>(['', '', '']);
    const [protections, setProtections] = useState<string[][]>([[], [], []]);
    const [agreements, setAgreements] = useState<boolean[]>([false, false, false]);

    const protectionOptions = ["Time", "Health", "Peace", "Focus"];

    const addTriggerRow = () => {
        setTriggers([...triggers, {
            id: Date.now(),
            trigger: '',
            sensation: '',
            impulse: '',
            regulation: ''
        }]);
    };

    const updateTrigger = (id: number, field: keyof TriggerEntry, value: string) => {
        setTriggers(triggers.map(t => t.id === id ? { ...t, [field]: value } : t));
    };

    const updateStandard = (index: number, value: string) => {
        const newStandards = [...standards];
        newStandards[index] = value;
        setStandards(newStandards);
    };

    const toggleProtection = (index: number, option: string) => {
        const newProtections = [...protections];
        const current = newProtections[index];
        if (current.includes(option)) {
            newProtections[index] = current.filter(o => o !== option);
        } else {
            newProtections[index] = [...current, option];
        }
        setProtections(newProtections);
    };

    const toggleAgreement = (index: number) => {
        const newAgreements = [...agreements];
        newAgreements[index] = !newAgreements[index];
        setAgreements(newAgreements);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text py-32 px-6 print:p-0 print:bg-white selection:bg-gold/30">
            <style>
                {`
                @media print {
                    @page { size: A4; margin: 0; }
                    body { margin: 0; -webkit-print-color-adjust: exact; background-color: white !important; color: #333333 !important; }
                    .print\\:hidden { display: none !important; }
                    .print\\:p-16 { padding: 4rem !important; }
                    .print\\:min-h-screen { min-height: 100vh !important; }
                    .print\\:shadow-none { box-shadow: none !important; }
                    .print\\:border-none { border: none !important; }
                    .print\\:text-charcoal { color: #333333 !important; }
                }
                .writing-lines {
                    background-image: repeating-linear-gradient(transparent, transparent 31px, rgba(212, 175, 55, 0.1) 31px, rgba(212, 175, 55, 0.1) 32px);
                    background-attachment: local;
                    line-height: 32px !important;
                    padding-top: 8px !important;
                }
                `}
            </style>

            <div className="max-w-4xl mx-auto flex flex-col items-center print:max-w-none">
                <Link 
                    to="/audit" 
                    className="self-start flex items-center gap-2 text-theme-text/60 hover:text-gold transition-colors text-xs uppercase tracking-widest mb-12 print:hidden"
                >
                    <ArrowLeft className="w-3 h-3" /> Back to Dashboard
                </Link>

                <div className="w-full bg-theme-bg/50 backdrop-blur-sm border border-theme-border shadow-2xl border-t-4 border-t-gold print:shadow-none print:border-none print:bg-white overflow-hidden">
                    {/* Preface Page */}
                    <section className="p-12 md:p-24 border-b border-theme-border print:min-h-screen print:flex print:flex-col print:justify-center">
                        <header className="mb-12 text-center">
                            <h1 className="font-serif text-4xl md:text-6xl text-theme-text mb-4 tracking-wide font-light uppercase print:text-charcoal">THE RECALIBRATION WORKSHEET</h1>
                            <h2 className="font-sans text-xs md:text-base text-gold tracking-[0.3em] uppercase font-medium">Phase 2: Clinical Intervention & Pattern Breaking</h2>
                        </header>
                        <div className="w-24 h-px bg-gold mx-auto mb-16 opacity-80" />
                        <div className="space-y-8 text-theme-text max-w-2xl mx-auto print:text-charcoal">
                            <h3 className="font-serif text-2xl italic text-center mb-10 text-theme-text/90 print:text-charcoal">The Preface: Moving Beyond Awareness</h3>
                            <p className="font-sans font-light leading-relaxed text-lg text-justify opacity-90">
                                Insight alone does not change a nervous system. You may now know your "Attachment ROI Score," 
                                but knowing you are losing bandwidth is not the same as reclaiming it. To move from Volatility 
                                to Security, you must disrupt the biological loops that keep you tethered to chaos.
                            </p>
                            <p className="font-sans font-light leading-relaxed text-lg text-justify opacity-90">
                                This worksheet is your <span className="font-medium text-gold">"Standard Operating Procedure" (SOP)</span> 
                                for emotional stabilization. Use it daily for the next 30 days to recalibrate your internal radar.
                            </p>
                        </div>
                    </section>

                    {/* Part 1: Trigger Tracker */}
                    <section className="p-8 md:p-16 border-b border-theme-border bg-theme-text/[0.02] print:min-h-screen print:bg-white print:p-16">
                        <div className="mb-10">
                            <h2 className="font-serif text-3xl text-theme-text mb-4 print:text-charcoal">Part 1: The Biological Trigger Tracker</h2>
                            <div className="w-16 h-0.5 bg-gold mb-6"></div>
                            <p className="font-sans font-light text-sm leading-relaxed text-theme-text/70 max-w-3xl print:text-charcoal">
                                High-achievers often attempt to "think" their way out of pain. This section forces you to build "Interoceptive Awareness"—recognizing the physical signal before the mental spiral begins.
                            </p>
                            <p className="font-sans font-light text-xs mt-4 italic text-taupe">
                                <strong>The Protocol:</strong> Whenever you feel a "ping" of relationship anxiety, do not reach for your phone. Reach for this log.
                            </p>
                        </div>

                        <div className="overflow-x-auto print:overflow-visible">
                            <table className="w-full border-collapse min-w-[700px] print:min-w-0">
                                <thead>
                                    <tr className="text-left font-serif text-gold text-lg">
                                        <th className="p-4 border-b border-gold font-normal">The Trigger<br/><span className="font-sans text-[10px] text-theme-text/40 uppercase tracking-widest not-italic print:text-charcoal/50">e.g., A late text</span></th>
                                        <th className="p-4 border-b border-gold font-normal">The Sensation<br/><span className="font-sans text-[10px] text-theme-text/40 uppercase tracking-widest not-italic print:text-charcoal/50">e.g., Tight chest</span></th>
                                        <th className="p-4 border-b border-gold font-normal">The Impulse<br/><span className="font-sans text-[10px] text-theme-text/40 uppercase tracking-widest not-italic print:text-charcoal/50">e.g., Frantic texting</span></th>
                                        <th className="p-4 border-b border-gold font-normal">The Regulation<br/><span className="font-sans text-[10px] text-theme-text/40 uppercase tracking-widest not-italic print:text-charcoal/50">e.g., Five-min walk</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {triggers.map((row) => (
                                        <tr key={row.id}>
                                            {['trigger', 'sensation', 'impulse', 'regulation'].map((field) => (
                                                <td key={field} className="border-b border-gold/20 p-0 align-top">
                                                    <textarea 
                                                        className="w-full bg-transparent p-4 font-sans font-light text-theme-text text-sm focus:outline-none focus:bg-gold/5 transition-colors resize-none overflow-hidden min-h-[104px] writing-lines print:text-charcoal"
                                                        value={row[field as keyof TriggerEntry] as string}
                                                        onChange={(e) => updateTrigger(row.id, field as keyof TriggerEntry, e.target.value)}
                                                        rows={3}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <button 
                                onClick={addTriggerRow}
                                className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-colors mx-auto print:hidden"
                            >
                                <Plus className="w-3 h-3" /> Add Curation Row
                            </button>
                        </div>
                    </section>

                    {/* Part 2: Standards Ledger */}
                    <section className="p-8 md:p-16 border-b border-theme-border print:min-h-screen print:p-16">
                        <div className="mb-12">
                            <h2 className="font-serif text-3xl text-theme-text mb-4 print:text-charcoal">Part 2: The Standard-Setting Ledger</h2>
                            <div className="w-16 h-0.5 bg-gold mb-8"></div>
                            <p className="font-sans font-light text-sm leading-relaxed text-theme-text/80 mb-8 max-w-2xl print:text-charcoal">
                                Boundaries are not "rules" for others; they are "investments" in your own equity. Based on your lowest score in the ROI Framework, define your three new Standards of Interaction.
                            </p>
                            <div className="bg-gold/5 p-6 border-l-2 border-gold italic">
                                <p className="font-serif text-theme-text/80 text-lg print:text-charcoal">
                                    Example: "I no longer accept 48-hour silences, because they cost me 15% of my weekly cognitive bandwidth."
                                </p>
                            </div>
                        </div>

                        <div className="space-y-16">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="group">
                                    <div className="flex flex-col md:flex-row md:items-baseline gap-4 mb-4">
                                        <span className="font-serif text-gold text-2xl italic whitespace-nowrap">Standard {["One", "Two", "Three"][i]}:</span>
                                        <textarea 
                                            value={standards[i]}
                                            onChange={(e) => updateStandard(i, e.target.value)}
                                            className="flex-grow bg-transparent border-b border-theme-border group-hover:border-gold/50 transition-colors pb-1 font-serif text-xl md:text-2xl text-theme-text focus:outline-none placeholder:text-theme-text/20 resize-none overflow-hidden min-h-[64px] writing-lines print:text-charcoal"
                                            placeholder="Define your interactive standard..."
                                            rows={2}
                                        />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 pl-4 md:pl-32">
                                        <span className="font-sans text-taupe italic text-xs tracking-widest uppercase">Protects my:</span>
                                        <div className="flex gap-2">
                                            {protectionOptions.map((opt) => (
                                                <button 
                                                    key={opt}
                                                    onClick={() => toggleProtection(i, opt)}
                                                    className={cn(
                                                        "text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-all duration-300",
                                                        protections[i].includes(opt) 
                                                            ? "border-gold bg-gold text-charcoal" 
                                                            : "border-theme-border text-taupe hover:text-gold hover:border-gold"
                                                    )}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Part 3: Implementation Contract */}
                    <section className="p-8 md:p-16 border-b border-theme-border bg-theme-text/[0.02] print:min-h-screen print:bg-white print:p-16">
                        <div className="mb-10">
                            <h2 className="font-serif text-3xl text-theme-text mb-4 print:text-charcoal">Part 3: The 30-Day Implementation Contract</h2>
                            <div className="w-16 h-0.5 bg-gold mb-8"></div>
                            <p className="font-sans font-light text-sm text-theme-text/80 print:text-charcoal">
                                Transformation is a series of "If-Then" decisions. Commit to these three non-negotiables for the next 30 days.
                            </p>
                        </div>

                        <div className="space-y-6 mb-20">
                            {[
                                {if: 'I feel the urge to frantically seek reassurance,', then: 'I will wait 20 minutes and engage in one professional task first.'},
                                {if: 'I encounter "intermittent reinforcement" (hot and cold behavior),', then: 'I will label it as a "High-Volatility Asset" and withdraw my emotional capital.'},
                                {if: 'I feel bored by a consistent, secure man,', then: 'I will acknowledge it as "Safety" and give the connection 3 more dates to recalibrate my radar.'}
                            ].map((item, index) => (
                                <button 
                                    key={index}
                                    onClick={() => toggleAgreement(index)}
                                    className={cn(
                                        "w-full text-left p-8 border transition-all duration-500 relative group overflow-hidden",
                                        agreements[index] 
                                            ? "border-gold bg-gold/5" 
                                            : "border-theme-border hover:border-gold hover:bg-gold/5"
                                    )}
                                >
                                    <div className="absolute top-0 left-0 w-1 h-full bg-gold/30" />
                                    <div className="absolute top-8 right-8">
                                        <div className={cn(
                                            "w-6 h-6 border transition-all duration-300 flex items-center justify-center",
                                            agreements[index] ? "border-gold bg-gold" : "border-theme-border bg-theme-bg/50"
                                        )}>
                                            {agreements[index] && <Check className="w-4 h-4 text-theme-bg" />}
                                        </div>
                                    </div>
                                    <p className="font-serif text-xl text-theme-text/60 mb-3 group-hover:text-theme-text transition-colors print:text-charcoal/60">
                                        <span className="text-gold font-bold italic mr-3">If</span> {item.if}
                                    </p>
                                    <p className="font-serif text-2xl text-theme-text group-hover:text-gold transition-colors print:text-charcoal">
                                        <span className="text-gold font-bold italic mr-3">Then</span> {item.then}
                                    </p>
                                </button>
                            ))}
                        </div>

                        <div className="mt-24 text-center border-t border-gold/30 pt-16">
                            <h3 className="font-serif text-2xl italic text-theme-text mb-6 print:text-charcoal">The Closing Allocation</h3>
                            <p className="font-sans font-light text-base leading-relaxed max-w-2xl mx-auto mb-12 italic opacity-80 print:text-charcoal">
                                "You are the architect of your own environment. If the current architecture is failing, it is within your power to redraw the blueprints. True luxury is the ability to choose who has access to your peace."
                            </p>
                            <div className="flex flex-col items-center gap-4">
                                <h4 className="font-sans font-medium tracking-[0.4em] text-sm text-theme-text uppercase block print:text-charcoal">She Deserves Better</h4>
                                <div className="w-12 h-px bg-gold"></div>
                                <p className="font-serif italic text-gold text-lg">Know Yourself. Choose Better.</p>
                            </div>
                        </div>
                    </section>

                    {/* Print/Save Action */}
                    <section className="p-16 bg-charcoal text-cream text-center print:hidden">
                        <h3 className="font-serif text-2xl text-gold mb-8 italic tracking-wide">Protocol Finalization</h3>
                        <div className="flex flex-col items-center gap-6">
                            <button 
                                onClick={handlePrint}
                                className="btn-gold px-12 py-5 flex items-center gap-3 transition-transform hover:scale-105 active:scale-95"
                            >
                                <Download className="w-4 h-4" /> Finalize & Export PDF
                            </button>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-taupe">Strict Maintenance Recommended for 30 cycles</p>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
